/**
 * Pure geometry for the venture dot canvases: random points in a ball, on a
 * spherical shell or in a tube, rotations, and the per-dot motion seeds
 * (breathing phase, frequency, wobble) that the frame renderer animates.
 * Coordinates are unit-space; the renderer scales them to the canvas.
 */

/**
 * Motion seeds for one dot, plus its eased pointer offset (ox, oy) at rest.
 * @param {() => number} [rnd]
 */
export const dotMotion = (rnd = Math.random) => ({
	phase: rnd() * Math.PI * 2,
	freq: 0.8 + rnd() * 1.8,
	jx: (rnd() - 0.5) * 0.06,
	jy: (rnd() - 0.5) * 0.06,
	jz: (rnd() - 0.5) * 0.06,
	ox: 0,
	oy: 0,
});

/**
 * A uniform random point inside a ball of radius r (rejection sampling).
 * @returns {[number, number, number]}
 */
export function randomInSphere(r, rnd = Math.random) {
	while (true) {
		const x = (rnd() * 2 - 1) * r;
		const y = (rnd() * 2 - 1) * r;
		const z = (rnd() * 2 - 1) * r;
		if (x * x + y * y + z * z <= r * r) return [x, y, z];
	}
}

/**
 * A uniform random point on the unit sphere (Marsaglia), pushed out to a
 * radius drawn from [minR, minR + spread).
 * @returns {[number, number, number]}
 */
export function randomOnShell(minR, spread, rnd = Math.random) {
	let x;
	let y;
	let s2;
	do {
		x = rnd() * 2 - 1;
		y = rnd() * 2 - 1;
		s2 = x * x + y * y;
	} while (s2 >= 1 || s2 === 0);
	const f = 2 * Math.sqrt(1 - s2);
	const rad = minR + rnd() * spread;
	return [x * f * rad, y * f * rad, (1 - 2 * s2) * rad];
}

// Two unit vectors perpendicular to `axis` and to each other.
const perpendicularBasis = ([ax, ay, az]) => {
	let u = Math.abs(ax) < 0.9 ? [0, -az, ay] : [az, 0, -ax];
	const ul = Math.hypot(u[0], u[1], u[2]);
	u = [u[0] / ul, u[1] / ul, u[2] / ul];
	const v = [ay * u[2] - az * u[1], az * u[0] - ax * u[2], ax * u[1] - ay * u[0]];
	return [u, v];
};

/**
 * A random point in a tube of radius tubeR around a unit axis, between
 * distances start and end from the origin.
 * @param {[number, number, number]} axis
 * @returns {[number, number, number]}
 */
export function randomInCylinder(axis, tubeR, start, end, rnd = Math.random) {
	const t = start + rnd() * (end - start);
	const angle = rnd() * Math.PI * 2;
	const rr = Math.sqrt(rnd()) * tubeR;
	const [u, v] = perpendicularBasis(axis);
	const cx = Math.cos(angle) * rr;
	const cy = Math.sin(angle) * rr;
	const at = (k) => axis[k] * t + u[k] * cx + v[k] * cy;
	return [at(0), at(1), at(2)];
}

/** Rotates a point around the Y axis by angle a (radians). */
export function rotateY(x, y, z, a) {
	const c = Math.cos(a);
	const s = Math.sin(a);
	return [c * x + s * z, y, -s * x + c * z];
}

/** Rotates a point around the X axis by angle a (radians). */
export function rotateX(x, y, z, a) {
	const c = Math.cos(a);
	const s = Math.sin(a);
	return [x, c * y - s * z, s * y + c * z];
}
